import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Shield } from 'lucide-react';

export default function FirstLoginPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const { changePassword, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error(t('error'), { description: 'Les mots de passe ne correspondent pas' });
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t('error'), { description: 'Le mot de passe doit contenir au moins 8 caractères' });
      return;
    }

    setLoading(true);
    
    try {
      await changePassword(currentPassword, newPassword);
      toast.success(t('success'), { description: 'Mot de passe modifié avec succès' });
      navigate('/pin-setup');
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(t('error'), { 
        description: error.response?.data?.detail || 'Erreur lors du changement de mot de passe' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md">
        {/* Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <Shield className="w-10 h-10 text-[#D4AF37]" />
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('first_login_title')}
          </h1>
          <p className="text-muted-foreground">
            {t('first_login_subtitle')}
          </p>
        </div>

        {/* Welcome message */}
        <div className="glass rounded-sm p-4 mb-8">
          <p className="text-sm text-center">
            Bienvenue <span className="text-[#D4AF37] font-medium">{user?.first_name} {user?.last_name}</span>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="current" className="text-sm font-medium">
              {t('current_password')}
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="current"
                type={showPasswords ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Mot de passe provisoire"
                className="pl-10 h-12 bg-card border-border"
                required
                data-testid="current-password-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new" className="text-sm font-medium">
              {t('new_password')}
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="new"
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nouveau mot de passe"
                className="pl-10 h-12 bg-card border-border"
                required
                data-testid="new-password-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm" className="text-sm font-medium">
              {t('confirm_password')}
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="confirm"
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmer le mot de passe"
                className="pl-10 pr-10 h-12 bg-card border-border"
                required
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(!showPasswords)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPasswords ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Password requirements */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p>Le mot de passe doit contenir :</p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li className={newPassword.length >= 8 ? 'text-emerald-500' : ''}>
                Au moins 8 caractères
              </li>
              <li className={/[A-Z]/.test(newPassword) ? 'text-emerald-500' : ''}>
                Une lettre majuscule
              </li>
              <li className={/[0-9]/.test(newPassword) ? 'text-emerald-500' : ''}>
                Un chiffre
              </li>
            </ul>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 btn-gold text-base"
            data-testid="change-password-btn"
          >
            {loading ? t('loading') : t('confirm')}
          </Button>
        </form>
      </div>
    </div>
  );
}
